/**
 * Preferred location localStorage utilities.
 *
 * The preferred location is a client-only UI shortcut (not persisted to
 * Orbit). It's scoped per (alias, businessUnitId) so switching BUs
 * swaps which value is active.
 *
 * Keys are managed via GravionStorageKeys.preferredLocation(alias, buId)
 * which follows the shared BaseStorageKeys convention (namespaced +
 * versioned). The resolved key format is:
 *   gravion:preferred-location:<alias>:<businessUnitId>:v1
 *
 * Reactivity: `writePreferredLocation` notifies same-tab subscribers so
 * every consumer of `usePreferredLocationValue` (UserProvider, filter
 * hooks) re-reads immediately — no page refresh needed. Cross-tab sync
 * comes from the browser `storage` event inside the hook.
 *
 * Consumers:
 *   - `UserProvider` exposes `preferredLocation` in context via
 *     `usePreferredLocationValue` (reactive).
 *   - `ProfilePreferencesPage` writes via writePreferredLocation, which
 *     notifies subscribers.
 *   - `useLocationFilter()` hook wraps the convention into reusable state.
 */

import { GravionStorageKeys } from 'src/v2/shared/storage/keys';

/**
 * Same-tab subscribers notified whenever writePreferredLocation runs.
 * The browser `storage` event only fires in OTHER tabs, so an explicit
 * notification channel is needed for the writing tab itself.
 */
const subscribers = new Set<() => void>();

/**
 * Subscribe to same-tab preferred-location writes. Returns an
 * unsubscribe function. Shaped for useSyncExternalStore.
 */
export const subscribeToPreferredLocation = (listener: () => void): (() => void) => {
  subscribers.add(listener);
  return () => subscribers.delete(listener);
};

/**
 * Read the persisted preferred location for (alias, BU) from
 * localStorage. Returns `undefined` when no key can be built or the
 * read fails. Safe to call outside React (no hooks).
 */
export const readPreferredLocation = (
  alias: string,
  businessUnitId: string | undefined,
): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  const descriptor = GravionStorageKeys.preferredLocation(alias, businessUnitId);
  if (!descriptor) return undefined;
  try {
    const raw = window.localStorage.getItem(descriptor.resolved);
    if (raw === null) return undefined;
    // useLocalStorage JSON-serialises values; unwrap the string.
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Persist the preferred location for (alias, BU) to localStorage and
 * notify same-tab subscribers. Writes the value JSON-encoded to match
 * useLocalStorage's format. Pass `undefined` to clear the key.
 * Safe to call outside React (no hooks).
 */
export const writePreferredLocation = (
  alias: string,
  businessUnitId: string | undefined,
  next: string | undefined,
): void => {
  if (typeof window === 'undefined') return;
  const descriptor = GravionStorageKeys.preferredLocation(alias, businessUnitId);
  if (!descriptor) return;
  try {
    if (next !== undefined) {
      window.localStorage.setItem(descriptor.resolved, JSON.stringify(next));
    } else {
      window.localStorage.removeItem(descriptor.resolved);
    }
  } catch {
    // Storage unavailable (private mode, quota). Silently ignore.
  }
  subscribers.forEach((listener) => listener());
};

export { syncPreferredLocationSeed } from './preferredLocationSeed';
export { usePreferredLocationValue } from './usePreferredLocationValue';
export { useLocationFilter } from './useLocationFilter';
export type { UseLocationFilterResult } from './useLocationFilter';
